import { useCallback, useEffect, useRef, useState } from 'react';
import { getLevelColor, getLitLevelCount } from './useLevelLadder';

const createTrackingState = () => ({
	hasHydrated: false,
	previousLevelId: null,
});

const getHasLeveledUp = ({ previousLevelId, currentLevelId, flameLevels }) => {
	const previousIndex = flameLevels.findIndex(
		(level) => level.id === previousLevelId,
	);
	const nextIndex = flameLevels.findIndex(
		(level) => level.id === currentLevelId,
	);
	const canCompareLevels = previousIndex >= 0 && nextIndex >= 0;

	if (!canCompareLevels) {
		return false;
	}

	return nextIndex > previousIndex;
};

const trackLevelUpTransition = ({
	trackingRef,
	currentLevelId,
	currentLevel,
	flameLevels,
}) => {
	if (!currentLevelId) {
		trackingRef.current = createTrackingState();
		return null;
	}

	if (!trackingRef.current.hasHydrated) {
		trackingRef.current = {
			hasHydrated: true,
			previousLevelId: currentLevelId,
		};
		return null;
	}

	const previousLevelId = trackingRef.current.previousLevelId;
	trackingRef.current = {
		hasHydrated: true,
		previousLevelId: currentLevelId,
	};

	if (previousLevelId === currentLevelId) {
		return null;
	}

	const canCelebrate =
		getHasLeveledUp({
			previousLevelId,
			currentLevelId,
			flameLevels,
		}) && Boolean(currentLevel?.name);

	if (!canCelebrate) {
		return null;
	}

	return currentLevel;
};

export const useLevelUpCelebration = (ladder, { conversationKey } = {}) => {
	const { currentLevel, currentLevelId, flameLevels, litCount, accentColor } =
		ladder;
	const [isLevelUpOpen, setIsLevelUpOpen] = useState(false);
	const [celebratedLevel, setCelebratedLevel] = useState(null);
	const trackingRef = useRef(createTrackingState());

	useEffect(() => {
		trackingRef.current = createTrackingState();
		setIsLevelUpOpen(false);
		setCelebratedLevel(null);
	}, [conversationKey]);

	useEffect(() => {
		const leveledUpLevel = trackLevelUpTransition({
			trackingRef,
			currentLevelId,
			currentLevel,
			flameLevels,
		});

		if (!leveledUpLevel) {
			return;
		}

		setCelebratedLevel(leveledUpLevel);
		setIsLevelUpOpen(true);
	}, [currentLevel, currentLevelId, flameLevels]);

	const celebratedLitCount = celebratedLevel
		? getLitLevelCount(flameLevels, celebratedLevel.id) || litCount
		: litCount;
	const celebratedColor = getLevelColor(celebratedLevel, accentColor);
	const closeLevelUp = useCallback(() => setIsLevelUpOpen(false), []);

	return {
		isLevelUpOpen,
		celebratedLevel,
		celebratedLitCount,
		celebratedColor,
		closeLevelUp,
	};
};
